import { useRef, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, useReducedMotion, useSpring } from 'framer-motion';

/**
 * A CTA link that's subtly "magnetic": it springs toward the cursor while hovered and
 * settles back on leave. Pure decoration tied to a spring (emil-design-eng: never bind
 * decorative motion to raw input). Falls back to a plain link under reduced motion or on
 * touch (no hover). Compose the visual button styles via `className` (e.g. `.press`).
 */
export function MagneticButton({
  to,
  children,
  className,
  strength = 0.35,
}: {
  to: string;
  children: ReactNode;
  className?: string;
  strength?: number;
}) {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const x = useSpring(mx, { stiffness: 220, damping: 16, mass: 0.5 });
  const y = useSpring(my, { stiffness: 220, damping: 16, mass: 0.5 });

  if (reduced) {
    return (
      <Link to={to} className={className}>
        {children}
      </Link>
    );
  }

  function handleMove(e: React.PointerEvent<HTMLDivElement>) {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    mx.set((e.clientX - (r.left + r.width / 2)) * strength);
    my.set((e.clientY - (r.top + r.height / 2)) * strength);
  }
  function reset() {
    mx.set(0);
    my.set(0);
  }

  return (
    <motion.div
      ref={ref}
      style={{ x, y }}
      className="inline-flex"
      onPointerMove={handleMove}
      onPointerLeave={reset}
    >
      <Link to={to} className={className}>
        {children}
      </Link>
    </motion.div>
  );
}
